import React from 'react';
import { Box, Spinner } from '@chakra-ui/react';

interface ImageDocumentRendererProps {
	src: string;
	alt?: string;
	loading?: boolean;
	onBackgroundClick?: () => void;
	containerProps?: React.ComponentProps<typeof Box>;
	spinnerColor?: string;
	spinnerSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
	scale?: number; // allow zoom parity with PDF
}

/**
 * Simple image renderer used by both compact and full modes.
 * Focuses on WHY: consistent image preview with optional zoom, no pagination.
 */
const ImageDocumentRenderer: React.FC<ImageDocumentRendererProps> = ({
	src,
	alt = 'Image preview',
	loading,
	onBackgroundClick,
	containerProps = {},
	spinnerColor = 'primary',
	spinnerSize = 'lg',
	scale = 1,
}) => {
	const handleContainerClick = (e: React.MouseEvent) => {
		if (e.target === e.currentTarget && onBackgroundClick) onBackgroundClick();
	};

	return (
		<Box
			display="flex"
			justifyContent="center"
			alignItems="center"
			onClick={handleContainerClick}
			{...containerProps}
		>
			{loading && <Spinner color={spinnerColor} size={spinnerSize} />}
			<img
				src={src}
				alt={alt}
				style={{
					transform: `scale(${scale})`,
					maxWidth: '100%',
					// Without a height cap a portrait image renders taller than the viewport and
					// overflows (no fit) on mobile; cap to the container so it scales down to fit.
					maxHeight: '100%',
					width: 'auto',
					height: 'auto',
					objectFit: 'contain',
					pointerEvents: 'auto',
					display: 'block',
				}}
			/>
		</Box>
	);
};

export default ImageDocumentRenderer;
